import { Controller } from "@hotwired/stimulus"
import videojs from "video.js"
import 'videojs-mobile-ui/dist/videojs-mobile-ui.css';
import 'videojs-mobile-ui';
import { post } from "@rails/request.js"

export default class extends Controller {
  static values = {
    resourceId: String,
    descriptionId: String,
    demoId: Number,
    referrer: { type: String, default: "player" },
    locale: { type: String, default: "en" }
  }

  resourceElement = document.getElementById(this.resourceIdValue);
  resourceDataId = this.resourceIdValue.substring(13) // Assumes a prefix of video_player_
  descriptionElement = document.getElementById(this.descriptionIdValue);
  visibleCounts = { [this.descriptionIdValue]: 0 }
  
  // Video engagement tracking properties - using time ranges for accurate tracking
  watchedRanges = [] // Array of [start, end] time ranges
  currentRangeStart = null // Start time of current viewing session
  lastRecordedTime = null // Last time we recorded
  
  connect() {
    let player = videojs(this.resourceIdValue);
    this.player = player; // Store reference for cleanup
    this.addSubtitleListener(player);
    // Enable mobile friendly touch overlay on video
    player.mobileUi();
    // Reveal description text on scroll
    player.on('loadstart', this.observeScroll.bind(this));
    player.on('ready', () => { window.scrollTo(0,1) } );

    player.on('play', (e) => { 
      this.logEvent(e, player)
      this.startTracking(player)
    })
    player.on('pause', (e) => { 
      this.logEvent(e, player)
      this.stopTracking(player)
      this.trackWatchTime(player)
    })
    player.on('ended', (e) => {
      this.logEvent(e, player)
      this.stopTracking(player)
      this.trackWatchTime(player)
    })
    player.on('seeked', (e) => { 
      this.logEvent(e, player)
      this.handleSeek(player)
    })
    player.on("subtitle.show", (e) => { this.logEvent(e, player) })
    player.on("subtitle.hide", (e) => { this.logEvent(e, player) })
    
    // Add timeupdate listener for engagement tracking (only for non-demo videos)
    if (this.demoIdValue === 0) {
      player.on('timeupdate', (e) => { this.updateWatchedTime(player) })
      
      // Track watch time before page unload (external navigation, tab close)
      this.beforeUnloadHandler = () => {
        this.trackWatchTimeBeforeUnload(this.player)
      }
      window.addEventListener('beforeunload', this.beforeUnloadHandler)
      window.addEventListener('pagehide', this.beforeUnloadHandler)

      // Track when video is backgrounded/foregrounded (tab switching, minimizing, etc)
      // Only log these events if video is actually playing
      this.visibilityChangeHandler = () => {
        if (!this.player || this.player.paused()) return

        if (document.visibilityState === 'hidden') {
          this.logEvent({ type: 'backgrounded' }, this.player)
        } else if (document.visibilityState === 'visible') {
          this.logEvent({ type: 'foregrounded' }, this.player)
        }
      }
      window.addEventListener('visibilitychange', this.visibilityChangeHandler)

      // Track watch time before Turbo navigation (back button, internal links)
      this.turboBeforeVisitHandler = () => {
        this.trackWatchTimeBeforeUnload(this.player)
      }
      document.addEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
      document.addEventListener('turbo:before-render', this.turboBeforeVisitHandler)
    }
  }

  play(){
    let player = videojs(this.resourceIdValue);
    player.play();
  }

  pause(){
    let player = videojs(this.resourceIdValue);
    player.pause();
  }

  showTextAfterScroll(entries) {
    entries.forEach((entry) => {
      if(this.entryVisibleAfterLoad(entry) || this.entryVisibleOnLoad(entry)) {
        this.descriptionElement.classList.toggle('bg-white');
      }

      if(entry.isIntersecting) {
        this.visibleCounts[this.descriptionIdValue]++;
      }
    })
  }

  observeScroll(_event) {
    // Only observe scroll if description element exists
    if (!this.descriptionElement) {
      return;
    }

    const options = {
      rootMargin: "0px",
      threshold: 0.2,
    }

    const observer = new IntersectionObserver(this.showTextAfterScroll.bind(this), options);
    observer.observe(this.descriptionElement);
  };

  entryVisibleAfterLoad(entry) {
   return  entry.isIntersecting && entry.intersectionRatio >= 1 && this.visibleCounts[this.descriptionIdValue] > 0
  }

  entryVisibleOnLoad(entry) {
    // Using minimum screen height for iPhoneSE 3rd Gen
    return window.screen.height >= 667;
  }

  addSubtitleListener(player) {
    let timeout;
    player.textTracks().on("change", function action(event) {
      clearTimeout(timeout)

      const showing = this.tracks_.filter(function (track) {
        if (track.kind === "captions" && track.mode === "showing") {
            return true;
        }
      })[0]

      timeout = setTimeout(function () {
        if(showing) {
          player.trigger("subtitle.show")
        } else {
          player.trigger("subtitle.hide")
        }
      }, 10);
    })
  }

  async logEvent(event, player) {
    const payload = {
      event: `video.${event["type"]}`,
      time: player.currentTime(),
      resource_id: this.resourceDataId,
      referrer: this.referrerValue,
      locale: this.localeValue
    };

    // If no demoId, log to user_activities
    if (this.demoIdValue !== 0) {
      await post(`/demos/${this.demoIdValue}/activity`, {
        body: JSON.stringify(payload)
      });
    } else {
      await post('/user_activities/activity', {
        body: JSON.stringify(payload)
      });
    }
  }

  startTracking(player) {
    if (this.demoIdValue !== 0) return
    const currentTime = player.currentTime()
    this.currentRangeStart = currentTime
    this.lastRecordedTime = currentTime
  }

  stopTracking(player) {
    if (this.demoIdValue !== 0 || this.currentRangeStart === null) return
    const currentTime = player.currentTime()
    
    // Only add range if there was actual playback
    if (currentTime > this.currentRangeStart) {
      this.watchedRanges.push([this.currentRangeStart, currentTime])
    }
    
    this.currentRangeStart = null
    this.lastRecordedTime = null
  }

  handleSeek(player) {
    if (this.demoIdValue !== 0) return
    
    const currentTime = player.currentTime()
    
    // If we were tracking, close the current range
    if (this.currentRangeStart !== null && this.lastRecordedTime !== null) {
      // Only add if we watched something before seeking
      if (this.lastRecordedTime > this.currentRangeStart) {
        this.watchedRanges.push([this.currentRangeStart, this.lastRecordedTime])
      }
    }
    
    // Start new range at the seeked position if playing
    if (!player.paused()) {
      this.currentRangeStart = currentTime
      this.lastRecordedTime = currentTime
    } else {
      this.currentRangeStart = null
      this.lastRecordedTime = null
    }
  }

  updateWatchedTime(player) {
    if (this.demoIdValue !== 0 || this.currentRangeStart === null) return
    
    const currentTime = player.currentTime()
    
    // Detect discontinuities (like seeking during playback without triggering seeked event)
    if (this.lastRecordedTime !== null && Math.abs(currentTime - this.lastRecordedTime) > 1.5) {
      // Close current range and start new one
      if (this.lastRecordedTime > this.currentRangeStart) {
        this.watchedRanges.push([this.currentRangeStart, this.lastRecordedTime])
      }
      this.currentRangeStart = currentTime
    }
    
    this.lastRecordedTime = currentTime
  }

  mergeRanges(ranges) {
    if (ranges.length === 0) return []
    
    // Sort ranges by start time
    const sorted = [...ranges].sort((a, b) => a[0] - b[0])
    const merged = [sorted[0]]
    
    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i]
      const lastMerged = merged[merged.length - 1]
      
      // If current range overlaps with last merged range, merge them
      if (current[0] <= lastMerged[1]) {
        lastMerged[1] = Math.max(lastMerged[1], current[1])
      } else {
        merged.push(current)
      }
    }
    
    return merged
  }

  calculateWatchTime(player) {
    // Include current viewing session if playing
    let ranges = [...this.watchedRanges]
    
    if (this.currentRangeStart !== null && this.lastRecordedTime !== null) {
      const currentTime = player ? player.currentTime() : this.lastRecordedTime
      if (currentTime > this.currentRangeStart) {
        ranges.push([this.currentRangeStart, currentTime])
      }
    }
    
    // Merge overlapping ranges to avoid double-counting rewatched sections
    const merged = this.mergeRanges(ranges)
    
    // Calculate total time from merged ranges
    return merged.reduce((total, [start, end]) => total + (end - start), 0)
  }

  async trackWatchTime(player) {
    // Only track watch time for non-demo videos (UserActivity, not DemoActivity)
    if (this.demoIdValue === 0 && this.watchedRanges.length > 0) {
      const videoDuration = player.duration()
      
      const payload = {
        event: 'video.watch_time',
        resource_id: this.resourceDataId,
        referrer: this.referrerValue,
        locale: this.localeValue,
        watched_ranges: this.watchedRanges,
        video_duration_seconds: Math.round(videoDuration * 100) / 100,
        current_time: Math.round(player.currentTime() * 100) / 100
      };

      await post('/user_activities/activity', {
        body: JSON.stringify(payload)
      });
      
      // Clear ranges after successful send to avoid duplicate tracking
      this.watchedRanges = [];
      this.currentRangeStart = null;
      this.lastRecordedTime = null;
    }
  }

  trackWatchTimeBeforeUnload(player) {
    // Use sendBeacon for reliable delivery during page unload
    if (this.demoIdValue !== 0) return;

    // Important: Stop tracking to capture current viewing session
    // This adds the current range (if playing) to watchedRanges
    this.stopTracking(player);

    // Only send if there are ranges to track
    if (this.watchedRanges.length > 0) {
      const videoDuration = player.duration()
      
      const payload = {
        event: 'video.watch_time',
        resource_id: this.resourceDataId,
        referrer: this.referrerValue,
        locale: this.localeValue,
        watched_ranges: this.watchedRanges,
        video_duration_seconds: Math.round(videoDuration * 100) / 100,
        current_time: Math.round(player.currentTime() * 100) / 100
      };

      // sendBeacon requires a Blob with proper content type
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon('/user_activities/activity', blob);
      
      // Clear ranges after sending
      this.watchedRanges = [];
      this.currentRangeStart = null;
      this.lastRecordedTime = null;
    }
  }

  disconnect() {
    // Clean up event listeners when controller is disconnected
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
      window.removeEventListener('pagehide', this.beforeUnloadHandler)
    }

    if (this.visibilityChangeHandler) {
      window.removeEventListener('visibilitychange', this.visibilityChangeHandler)
    }

    if (this.turboBeforeVisitHandler) {
      document.removeEventListener('turbo:before-visit', this.turboBeforeVisitHandler)
      document.removeEventListener('turbo:before-render', this.turboBeforeVisitHandler)
    }
  }

}
